let db;

// Firefox throws "Permission denied to access property 'all'" inside Dexie's
// BroadcastChannel onmessage handler when cross-context objects arrive (e.g. from
// a browser extension). The error escapes our try/catch blocks because it fires
// asynchronously inside an event handler. Suppress it here before it reaches Rollbar.
if (typeof window !== 'undefined') {
  window.addEventListener('error', function(event) {
    if (event.message === 'Permission denied to access property "all"') {
      event.preventDefault();
    }
  }, true);
}

export default class LocalDB {
  constructor() {
    console.log("localdb construct");
    this.running = false;
    this.reopening = false;
    this.idbBlocked = false;
    this.directUploadOnly = false;
    this.reportedIdbFailure = false;
    this.pendingDirectUploads = {};
    this.pendingDirectUploadRequests = {};
  }

  direct_upload_key(recording_id, attendee_id) {
    return `${recording_id}_${attendee_id}`;
  }

  queue_direct_upload_file(file, recording_id, attendee_id) {
    const key = this.direct_upload_key(recording_id, attendee_id);
    if (!this.pendingDirectUploads[key]) {
      this.pendingDirectUploads[key] = [];
    }
    this.pendingDirectUploads[key].push(file);
  }

  flush_pending_direct_uploads(recording_id, attendee_id) {
    const key = this.direct_upload_key(recording_id, attendee_id);
    const files = this.pendingDirectUploads[key] || [];
    if (files.length === 0) return;

    const fileInput = $(".directUpload").find("input:file");
    if (
      typeof $("").fileupload === "undefined" ||
      fileInput.length === 0 ||
      fileInput.data("blueimp-fileupload") === undefined
    ) {
      return;
    }

    delete this.pendingDirectUploads[key];
    files.forEach(function (file) {
      fileInput.fileupload("add", { files: [file] });
    });
  }

  request_direct_upload_form(recording_id, attendee_id, onError) {
    const key = this.direct_upload_key(recording_id, attendee_id);
    if (this.pendingDirectUploadRequests[key]) return;

    this.pendingDirectUploadRequests[key] = true;
    var room_id = $("#room_id").attr("data-room-id");
    Rails.ajax({
      url: `/recordings/${recording_id}/attendee/${attendee_id}/finish_post_url.js?room_id=${room_id}`,
      type: "get",
      success: () => {
        delete this.pendingDirectUploadRequests[key];
      },
      error: (data) => {
        delete this.pendingDirectUploadRequests[key];
        if (onError) onError(data);
      },
    });
  }

  async load_local_db(forceRecreate = false) {
    console.log("load db");

    // IndexedDB permanently unavailable (e.g. Safari private browsing) — don't retry
    if (this.idbBlocked || this.directUploadOnly) return;

    // Probe IndexedDB availability before creating any Dexie instance.
    // Some Safari versions throw SecurityError synchronously, others fire it
    // on the request's error event asynchronously — this handles both.
    if (!window.indexedDB) {
      this.idbBlocked = true;
      console.warn("IndexedDB not available in this browser");
      $("#idb_blocked").show();
      return;
    }
    const idbAvailable = await new Promise((resolve) => {
      try {
        const probe = window.indexedDB.open("__idb_probe__");
        probe.onsuccess = () => { probe.result.close(); resolve(true); };
        probe.onerror = (e) => {
          resolve(probe.error && probe.error.name !== "SecurityError");
        };
      } catch (e) {
        resolve(e.name !== "SecurityError");
      }
    });
    if (!idbAvailable) {
      this.idbBlocked = true;
      console.warn("IndexedDB blocked by browser security context (e.g. private browsing)");
      $("#idb_blocked").show();
      return;
    }

    // Prevent multiple simultaneous reopen attempts
    if (this.reopening) {
      console.log("Already reopening db, skipping");
      return;
    }

    // If db exists and is open, don't recreate it (unless forced)
    if (!forceRecreate && typeof db !== "undefined" && db.isOpen()) {
      console.log("DB already open, skipping reload");
      return;
    }

    this.reopening = true;

    try {
      // Close existing database if it exists
      if (typeof db !== "undefined") {
        try {
          await db.close();
          console.log("Closed existing db");
        } catch (closeError) {
          console.log("Error closing db:", closeError.message);
        }
      }

      // If force recreate, delete the database first
      if (forceRecreate) {
        console.log("Force recreating database");
        try {
          await Dexie.delete("Recordings");
          console.log("Deleted old database");
        } catch (deleteError) {
          console.log("Error deleting db:", deleteError.message);
        }
      }

      // Create new database instance
      db = new Dexie("Recordings");
      db.version(2).stores({
        recordings: "++id,recording_id",
        blobs: "++id,recording_id,filename,attendee_id",
      });

      // Open the database
      await db.open();
      global.db = db; // Make db globally accessible
      console.log("DB opened successfully");
    } catch (error) {
      console.error("Error loading db:", error);

      if (error.name === "SecurityError") {
        // Browser is blocking IndexedDB (e.g. Safari private browsing, blocked cookies).
        // Report once and stop all future attempts — retrying is pointless.
        this.idbBlocked = true;
        if (!this.reportedIdbFailure) {
          this.reportedIdbFailure = true;
          Rollbar.error("load_local_db_error", error.message);
        }
        return;
      }

      if (error.name === "QuotaExceededError" || error.message.includes("QuotaExceededError") || error.message.includes("full disk")) {
        // Device is out of disk space — retrying is pointless.
        this.idbBlocked = true;
        if (!this.reportedIdbFailure) {
          this.reportedIdbFailure = true;
          Rollbar.error("load_local_db_error", error.message);
          alert("Your device's storage is full. We are unable to save your recording locally. Please free up disk space and try again.");
        }
        return;
      }

      // If this is not already a force recreate attempt, try once more with force
      if (
        !forceRecreate &&
        (error.name === "UnknownError" ||
          error.message.includes("Internal error"))
      ) {
        console.log(
          "Attempting to force recreate database due to UnknownError",
        );
        this.reopening = false;
        return await this.load_local_db(true);
      }

      // Chrome occasionally leaves IndexedDB in a broken state for the rest of the
      // session even after force recreation. Fall back to direct uploads instead
      // of repeatedly throwing and breaking the recorder.
      if (
        error.name === "UnknownError" ||
        error.message.includes("Internal error")
      ) {
        this.idbBlocked = true;
        this.directUploadOnly = true;
        if (!this.reportedIdbFailure) {
          this.reportedIdbFailure = true;
          Rollbar.error("indexeddb_unavailable_after_force_recreate", error.message);
          alert("Your browser's local recording storage is unavailable. We'll try uploading recordings directly instead.");
        }
        return;
      }

      if (!this.reportedIdbFailure) {
        this.reportedIdbFailure = true;
        Rollbar.error("load_local_db_error", error.message);
      }
      throw error;
    } finally {
      this.reopening = false;
    }
  }

  async check_for_recordings_to_upload() {
    if (this.running) { return; }
    if (this.idbBlocked || this.directUploadOnly) { return; }
    local_db.running = true;

    if (typeof db === "undefined" || !db.isOpen()) {
      try {
        await this.load_local_db();
      } catch (error) {
        console.error("check_for_recordings_to_upload: db not ready", error);
        local_db.running = false;
        return;
      }
    }

    db.recordings
      .toArray()
      .then(async function (recordings) {
        var recording = recordings[0];
        // window.uploading is used to show that recording is oging on
        if (
          $("[data-upload-setting]").attr("data-upload-setting") == "after" &&
          window.uploading == true
        ) {
          console.log("WAIT UNTIL AFTER");
          local_db.running = false;
          setTimeout(function () {
            local_db.check_for_recordings_to_upload();
          }, 5000);
        } else if (recording != undefined) {
          local_db.running = false;
          local_db.show_user_upload_details();
          local_db.upload_remaining_blobs(recording);
        } else {
          if (window.uploading) {
            console.log("still uploading so now waiting");
            local_db.running = false;
            setTimeout(function () {
              local_db.check_for_recordings_to_upload();
            }, 3000);
          } else {
            // check for any blobs left over
            var blobs = await db.blobs.toArray();
            var blob = blobs[0];
            if (blob != undefined) {
              local_db.running = false;
              db.recordings
                .put({
                  recording_id: blob["recording_id"],
                  attendee_id: blob["attendee_id"],
                })
                .then(function () {
                  console.log(
                    `Added recording id ${blob["recording_id"]} into the local db`,
                  );
                  local_db.check_for_recordings_to_upload();
                })
                .catch(function (error) {
                  Rollbar.error(
                    "Error Saving blob:(check_for_recordings_to_upload)" +
                      error,
                  );
                  console.log("ERROR SAVING BLOB4: " + error);
                  alert("ERROR SAVING BLOB (fr): " + error);
                });
            } else {
              local_db.running = false;
              console.log("No recordings");
              $(
                "#uploading_past_recordings, #still_uploading",
              ).hide();
              // Only treat this as a completed upload if one actually started this session
              if (window.localUploadPending) {
                window.localUploadComplete = true;
                if ($("#still_uploading_local").is(":visible")) {
                  $("#upload_progress_view").hide();
                  $("#upload_complete_view").show();
                }
                if (window.checkRecordingReady) window.checkRecordingReady();
              }
              $("#all_done_uplading").show();
            }
          }
        }
      })
      .catch(function (error) {
        local_db.running = false;
        alert(
          "Your browser may not support saving local recordings. Please update your browser or try the Chrome browser. \n\n Error:" +
            error,
        );
        console.log("CANT GET RECORDINGS: " + error);
      });
  }

  async show_user_upload_details() {
    if (!window.uploading && !window.localUploadPending && $("#still_uploading_local:visible").length == 0) {
      $("#uploading_past_recordings").show();
    }

    db.blobs.count().then(function (count) {
      $("#count_of_files_left_to_upload").text(count);
      $("#current_count_of_files_left_to_upload").text(count); // room page
    });
  }

  async upload_remaining_blobs(recording) {
    var recording_id = recording["recording_id"];
    var blob = await db.blobs.where({ recording_id: recording_id }).first();
    if (blob != undefined) {
      local_db.upload_blob(blob);
    } else {
      if (
        window.uploading &&
        $("#recording_id").attr("data-recording-id") == recording_id
      ) {
        setTimeout(function () {
          local_db.check_for_recordings_to_upload();
        }, 5000);
      } else {
        console.log("time to complete");
        local_db.complete_recording(recording);
      }
    }
  }

  complete_recording(recording) {
    console.log("complete recording");
    var recording_id = recording["recording_id"];
    var attendee_id = recording["attendee_id"];

    if (typeof options !== "undefined") {
      if (options.share_uid) {
        // maybe they didnt screen share for the following recordings in the room
        Rails.ajax({
          url: `/recordings/${recording_id}/attendee/${options.share_uid}/finished.js`,
          type: "post",
          error: function(xhr, status, error) {
            console.error("Failed to mark screen share recording as finished:", error);
            Rollbar.warning("complete_screen_share_recording_failed", {recording_id, share_uid: options.share_uid, status, error: error.toString()});
          }
        });
      }
    }

    Rails.ajax({
      url: `/recordings/${recording_id}/attendee/${attendee_id}/finished.js`,
      type: "post",
      success: function () {
        //delete from local
        db.recordings
          .where({ recording_id: recording_id, attendee_id: attendee_id })
          .delete()
          .then(function () {
            console.log(`deleted recording ${recording_id} from local storage`);
            local_db.check_for_recordings_to_upload();
          })
          .catch(function (error) {
            console.log("ERROR DELETING BLOB: " + error);
          });
      },
      error: function(xhr, status, error) {
        console.error("Failed to mark recording as finished:", error);
        Rollbar.error("complete_recording_failed", {recording_id, attendee_id, status, error: error.toString()});
        // Still try to delete from local storage and check for more recordings
        db.recordings
          .where({ recording_id: recording_id, attendee_id: attendee_id })
          .delete()
          .then(function () {
            console.log(`deleted recording ${recording_id} from local storage after error`);
            local_db.check_for_recordings_to_upload();
          })
          .catch(function (deleteError) {
            console.error("ERROR DELETING BLOB after finish error: " + deleteError);
            // Continue anyway
            local_db.check_for_recordings_to_upload();
          });
      }
    });
  }

  async upload_blob(blob) {
    var filename = blob["filename"];
    var blob_data = blob["blob_data"];
    var attendee_id = blob["attendee_id"];
    var recording_id = blob["recording_id"];
    if (blob_data == undefined) {
      console.log(
        "upload blobb " +
          blob["filename"] +
          " recording_id:" +
          blob["recording_id"] +
          " attendee_id:" +
          blob["attendee_id"],
      );
      Rollbar.error("blob_data_undefined", blob);
    }
    var file = new File([blob_data], filename, {
      lastModified: new Date().getTime(),
      type: blob_data.type,
    });

    if (file.size < 100) {
      Room.show_error_message(
        "Your browser tried to save a recording without any data. Please try a different browser like Google Chrome or else contact support at support@iris.fm.",
      );
      console.error("Too small upload: " + file.size);
      setTimeout(function () {
          Rails.ajax({
            url:
              `/recordings/${recording_id}/attendee/${attendee_id}/error.js?error=toosmallfileB` +
              file.size,
            type: "post",
          error: function(xhr, status, ajaxError) {
            console.error("Failed to report toosmallfileB error:", ajaxError);
            Rollbar.error("toosmallfileB_error_report_failed", {file_size: file.size, recording_id, attendee_id, status, error: ajaxError.toString()});
          }
        });
      }, 1000);
    }

    console.log(
      "upload blob " +
        blob["filename"] +
        " file size: " +
        file.size +
        " recording_id:" +
        blob["recording_id"] +
        " attendee_id:" +
        blob["attendee_id"],
    );

    if (
      typeof $("").fileupload === "undefined" ||
      $(".directUpload").find("input:file").length == 0 ||
      $(".directUpload").find("input:file").data("blueimp-fileupload") === undefined ||
      window.uploading_recording_id != blob["recording_id"] ||
      window.uploading_attendee_id != blob["attendee_id"]
    ) {
      this.queue_direct_upload_file(file, recording_id, attendee_id);
      this.request_direct_upload_form(recording_id, attendee_id, function (data) {
          Rollbar.error("failed_to_get_finish_post_url");
          console.log("failed getted post url");
          console.log(blob);
          setTimeout(function () {
            local_db.upload_blob(blob);
          }, 2000);
      });
    } else {
      $(".directUpload")
        .find("input:file")
        .fileupload("add", { files: [file] });
    }
  }

  async show_files_to_download() {
    var total_blobs = await db.blobs.count();
    if (total_blobs > 0) {
      var blobs = await db.blobs.toArray();
      var rows = blobs.map(function (blob) {
        var filename = blob["filename"];
        var recording_id = blob["recording_id"];
        return `<li data-blob-id="${blob["id"]}" class="py-4">
          <div class="flex items-center space-x-4">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">${filename}</p>
              <p class="text-xs text-gray-500 truncate">Recording id: ${recording_id}</p>
            </div>
            <div>
              <a href="#" onclick="local_db.download_blobfile(event, ${blob["id"]})" class="download_file inline-flex items-center shadow-sm px-3 py-1 border border-gray-300 text-sm leading-5 hover:bg-gray-100 font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"> Download </a>
              <a href="#" onclick="local_db.delete_blobfile(event, ${blob["id"]})" class="delete_file inline-flex items-center shadow-sm px-3 py-1 border border-gray-300 text-sm leading-5 hover:bg-gray-100 font-medium rounded-lg text-red-700 bg-white hover:bg-gray-50"> Delete </a>
            </div>
          </div>
        </li>`;
      });
      $("#filelist").append(rows.join(""));
    } else {
      var file_row = `<li class="py-4">
        <div class="flex items-center space-x-4">
          <div class="flex-1 min-w-0">
            <p class="text-sm text-gray-900 truncate">There are no recordings to download</p>
          </div>
          <div>
          </div>
        </div>
      </li>`;
      $("#filelist").append(file_row);
    }

    $("#download_files").show();
  }

  async download_blobfile(event, blob_id) {
    event.preventDefault();
    var blob = await db.blobs.where({ id: blob_id }).first();
    if (blob != undefined) {
      var attendee_id = blob["attendee_id"];
      var blob_data = blob["blob_data"];
      var recording_id = blob["recording_id"];
      var fileLink = document.createElement("a");
      fileLink.download =
        recording_id + "_" + attendee_id + "_" + blob["filename"];
      var blob_file = new Blob([blob_data], { type: blob_data.type });
      fileLink.href = URL.createObjectURL(blob_file);
      fileLink.click();
      URL.revokeObjectURL(fileLink.href);
    } else {
      console.log("cant find!");
      Rollbar.error("cant find blob!");
    }
  }

  async upload_file_direct(file, recording_id, attendee_id) {
    console.log(`upload_file_direct ${file.name} size: ${file.size} recording_id: ${recording_id} attendee_id: ${attendee_id}`);
    if (
      typeof $("").fileupload === "undefined" ||
      $(".directUpload").find("input:file").length == 0 ||
      $(".directUpload").find("input:file").data("blueimp-fileupload") === undefined ||
      window.uploading_recording_id != recording_id ||
      window.uploading_attendee_id != attendee_id
    ) {
      this.queue_direct_upload_file(file, recording_id, attendee_id);
      this.request_direct_upload_form(recording_id, attendee_id, function (data) {
        Rollbar.error("failed_to_get_finish_post_url_direct");
        console.error("failed to get post url for direct upload");
      });
    } else {
      $(".directUpload")
        .find("input:file")
        .fileupload("add", { files: [file] });
    }
  }

  async delete_blobfile(event, blob_id) {
    event.preventDefault();
    if (confirm("Are you sure you want to delete this file?") == true) {
      var blob = await db.blobs.where({ id: blob_id }).first();
      if (blob != undefined) {
        db.blobs
          .where({ id: blob_id })
          .delete()
          .then(function () {
            $(`[data-blob-id='${blob_id}']`).remove();
          })
          .catch(function (error) {
            console.log("ERROR - DELETING BLOBBB: " + error);
          });
      } else {
        console.log("cant find to delete!");
        Rollbar.error("cant find blob to delete!");
      }
    }
  }
}
